import React, { useState, useEffect } from 'react';
import API from '../../services/api';

const STATUS = {
  live:        { label: '🟢 Live',        color: '#00ff88' },
  coming_soon: { label: '🟡 Coming Soon', color: '#ffd700' },
  maintenance: { label: '🔴 Maintenance', color: '#ff6b6b' },
};

export default function Games() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    API.get('/games')
      .then(({ data }) => setGames(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const featured = games.filter(g => g.featured);
  const filtered = filter === 'all' ? games : games.filter(g => g.status === filter);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🎮 Games</h1>
        <p style={styles.subtitle}>Explore all available games on Blixo</p>
      </div>

      {featured.length > 0 && (
        <div style={styles.featuredSection}>
          <div style={styles.sectionLabel}>FEATURED</div>
          <div style={styles.featuredGrid}>
            {featured.map(game => (
              <div key={game._id} style={styles.featuredCard}>
                {game.imageUrl
                  ? <img src={game.imageUrl} alt={game.name} style={styles.featuredImg} />
                  : <div style={styles.featuredImgPlaceholder}>🎮</div>
                }
                <div style={styles.featuredOverlay}>
                  <h2 style={styles.featuredName}>{game.name}</h2>
                  <p style={styles.featuredGenre}>{game.genre} · {game.platform}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.filterRow}>
        {['all', 'live', 'coming_soon', 'maintenance'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ ...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {}) }}>
            {f === 'all' ? 'All Games' : STATUS[f].label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.loading}>Loading games...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎮</div>
          <div style={styles.emptyTitle}>No games here yet</div>
          <div style={styles.emptySub}>Check back soon!</div>
        </div>
      ) : (
        <div style={styles.grid}>
          {filtered.map(game => (
            <div key={game._id} style={styles.card}>
              <div style={styles.cardImgWrap}>
                {game.imageUrl
                  ? <img src={game.imageUrl} alt={game.name} style={styles.cardImg} />
                  : <div style={styles.cardImgPlaceholder}>🎮</div>
                }
                {game.featured && <span style={styles.featuredBadge}>⭐ Featured</span>}
                <span style={{ ...styles.statusBadge, color: STATUS[game.status]?.color, borderColor: STATUS[game.status]?.color }}>
                  {STATUS[game.status]?.label}
                </span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.cardTop}>
                  <h3 style={styles.cardName}>{game.name}</h3>
                  <span style={styles.genreBadge}>{game.genre}</span>
                </div>
                <p style={styles.cardDesc}>{game.description || 'No description available.'}</p>
                <span style={styles.metaTag}>📱 {game.platform}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { flex: 1, overflowY: 'auto', padding: '28px', fontFamily: "'Exo 2', sans-serif" },
  header: { marginBottom: '24px' },
  title: { margin: 0, fontSize: '26px', fontWeight: '700', color: '#e2e8f0', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '1px' },
  subtitle: { margin: '6px 0 0', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  featuredSection: { marginBottom: '28px' },
  sectionLabel: { color: 'rgba(245,166,35,0.6)', fontSize: '11px', fontWeight: '900', letterSpacing: '3px', marginBottom: '12px' },
  featuredGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  featuredCard: { borderRadius: '16px', overflow: 'hidden', position: 'relative', height: '180px', border: '1px solid rgba(255,215,0,0.2)' },
  featuredImg: { width: '100%', height: '100%', objectFit: 'cover' },
  featuredImgPlaceholder: { width: '100%', height: '100%', background: 'rgba(245,166,35,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '56px' },
  featuredOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' },
  featuredName: { margin: '4px 0 2px', color: '#fff', fontSize: '18px', fontWeight: '700' },
  featuredGenre: { margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '12px' },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  filterBtn: { padding: '8px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2', sans-serif" },
  filterBtnActive: { background: 'rgba(245,166,35,0.08)', color: '#f5a623', borderColor: 'rgba(245,166,35,0.3)' },
  loading: { textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: '60px' },
  empty: { textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.25)' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', marginBottom: '8px' },
  emptySub: { fontSize: '14px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '18px' },
  card: { background: '#0d1420', borderRadius: '16px', border: '1px solid rgba(245,166,35,0.08)', overflow: 'hidden' },
  cardImgWrap: { position: 'relative' },
  cardImg: { width: '100%', height: '150px', objectFit: 'cover', display: 'block' },
  cardImgPlaceholder: { width: '100%', height: '110px', background: 'rgba(245,166,35,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '44px' },
  featuredBadge: { position: 'absolute', top: '10px', left: '10px', background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.4)', color: '#ffd700', borderRadius: '6px', padding: '3px 9px', fontSize: '10px', fontWeight: '700' },
  statusBadge: { position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.55)', border: '1px solid', borderRadius: '6px', padding: '3px 9px', fontSize: '10px', fontWeight: '700' },
  cardBody: { padding: '16px' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' },
  cardName: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#e2e8f0' },
  genreBadge: { background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', color: '#00aaff', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', flexShrink: 0 },
  cardDesc: { color: 'rgba(255,255,255,0.3)', fontSize: '12px', lineHeight: '1.5', margin: '0 0 10px' },
  metaTag: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', color: 'rgba(255,255,255,0.35)' },
};
